import { Fragment, useState } from 'react';
import {
  Box,
  Grid,
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Typography,
  IconButton,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { colors, formatCurrency, SeatsRangeEnum, VehicleClass } from 'remote/global';
import { useTranslation } from 'react-i18next';
import { RemoveRedEyeOutlined } from '@mui/icons-material';
import { CalculatorResponseDtoSchemaType } from 'components/forms/CarrierFormFactory/types';
import { CostsTransparencyModal } from 'components/modals/PricingTransparencyModal/PricingTransparencyModal';

type SeatsRangeOption = {
  seatsFrom: number;
  seatsTo: number;
};

type VehicleClassRowProps = {
  result?: CalculatorResponseDtoSchemaType[];
  type: VehicleClass;
  seatsRangeOptions: SeatsRangeOption[];
};

const colorByClass = {
  [VehicleClass.BASIC]: colors.gray10,
  [VehicleClass.STANDARD]: colors.primaryBlue4,
  [VehicleClass.EXECUTIVE]: colors.gray6,
};

function VehicleClassRow({ result, type, seatsRangeOptions }: VehicleClassRowProps) {
  const { t, i18n } = useTranslation();
  const amountOfSizeOptions = seatsRangeOptions.length;

  const prices = seatsRangeOptions.map((price) =>
    result?.find(
      (r) =>
        r.seatsFrom === price.seatsFrom && r.seatsTo === price.seatsTo && r.vehicleClass === type
    )
  );

  return (
    <>
      <TableRow>
        <TableCell
          rowSpan={3}
          sx={{
            width: '86px',
            background: colorByClass[type],
            textTransform: 'uppercase',
            textAlign: 'center',
            color: colors.uiBlue600,
            mt: 10,
          }}
        >
          <Typography sx={{ transform: 'rotate(-90deg)' }} variant='subtitle2'>
            {t(`VehicleClass.${type}`)}
          </Typography>
        </TableCell>
        <TableCell variant='head'>{t('modals.priceCalculator.table.header.exclVat')}</TableCell>
        {Array.from({ length: amountOfSizeOptions }).map((_, index) => {
          const item = prices[index];
          return (
            <TableCell align='center' variant='head' key={uuidv4()}>
              {item?.finalPriceExclVat
                ? formatCurrency(item.finalPriceExclVat, i18n.language, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })
                : ''}
            </TableCell>
          );
        })}
      </TableRow>
      <TableRow>
        <TableCell variant='head'>{t('modals.priceCalculator.table.header.inclVat')}</TableCell>
        {Array.from({ length: amountOfSizeOptions }).map((_, index) => {
          const item = prices[index];
          return (
            <TableCell align='center' variant='head' key={uuidv4()}>
              {item?.finalPriceInclVat
                ? formatCurrency(item.finalPriceInclVat, i18n.language, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })
                : ''}
            </TableCell>
          );
        })}
      </TableRow>
      <TableRow>
        <TableCell variant='head'>
          {t('modals.priceCalculator.table.header.profitability')}
        </TableCell>
        {Array.from({ length: amountOfSizeOptions }).map((_, index) => {
          const item = prices[index];
          return (
            <TableCell align='center' variant='head' key={uuidv4()}>
              {item?.netProfitMargin ? `${item.netProfitMargin}%` : ''}
            </TableCell>
          );
        })}
      </TableRow>
    </>
  );
}

function PricingCalculatorTable({ result }: { result?: CalculatorResponseDtoSchemaType[] }) {
  const [isCostsTransparencyModalOpen, setIsCostsTransparencyModalOpen] = useState(false);
  const [transparencyPricing, setTransparencyPricing] = useState<CalculatorResponseDtoSchemaType>();
  const { t } = useTranslation();

  const seatsRangeOptions = Array.from(Object.values(SeatsRangeEnum)).map((item) => {
    const [seatsFrom, seatsTo] = item.split('_').slice(1).map(Number);
    return { seatsFrom, seatsTo };
  });

  const amountOfSizeOptions = seatsRangeOptions.length;

  const prices = seatsRangeOptions.map((price) =>
    result?.find((r) => r.seatsFrom === price.seatsFrom && r.seatsTo === price.seatsTo)
  );

  const handleClearCostsTransparencyModalState = () => {
    setIsCostsTransparencyModalOpen(false);
    setTransparencyPricing(undefined);
  };

  return (
    <>
      <CostsTransparencyModal
        open={isCostsTransparencyModalOpen}
        setOpen={setIsCostsTransparencyModalOpen}
        onClose={handleClearCostsTransparencyModalState}
        data={transparencyPricing!}
        title={`${t('modals.pricingTransparency.title')} (${transparencyPricing?.seatsFrom}-${
          transparencyPricing?.seatsTo
        })`}
      />
      <Grid item xs={12} sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: '600px', width: '100%' }}>
          <Box style={{ display: 'flex', alignItems: 'end' }} p={2} pt={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ borderBottom: 'none' }} />
                  <TableCell sx={{ borderBottom: 'none' }} />
                  {seatsRangeOptions.map(({ seatsFrom, seatsTo }) => {
                    return (
                      <TableCell
                        sx={{
                          padding: 0,
                          borderRight: `10px solid ${colors.gray8}`,
                          borderBottom: 'none',
                        }}
                        align='center'
                        variant='head'
                        key={`${seatsFrom}-${seatsTo}-seats`}
                      >
                        <Box
                          sx={{
                            padding: '12px 32px',
                            width: '100%',
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px',
                            background: colors.tiffanyBlue4,
                            color: colors.headerBlack,
                          }}
                        >
                          {seatsFrom}-{seatsTo}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell variant='head' sx={{ p: 0 }} />
                  <TableCell variant='head' sx={{ py: 1 }}>
                    {t('modals.priceCalculator.table.header.transparency')}
                  </TableCell>
                  {Array.from({ length: amountOfSizeOptions }).map((_, index) => {
                    const item = prices[index];
                    if (item == null)
                      return <TableCell variant='head' key={uuidv4()} sx={{ p: 0 }} />;
                    return (
                      <TableCell align='center' variant='head' key={uuidv4()} sx={{ p: 0 }}>
                        <IconButton
                          size='small'
                          onClick={() => {
                            setIsCostsTransparencyModalOpen(true);
                            setTransparencyPricing(item);
                          }}
                        >
                          <RemoveRedEyeOutlined />
                        </IconButton>
                      </TableCell>
                    );
                  })}
                </TableRow>
                {Array.from(Object.values(VehicleClass)).map((type) => {
                  return (
                    <Fragment key={type}>
                      <TableRow sx={{ height: 25 }} />
                      <VehicleClassRow
                        key={type}
                        seatsRangeOptions={seatsRangeOptions}
                        result={result}
                        type={type}
                      />
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Grid>
    </>
  );
}

export default PricingCalculatorTable;
